"use client";

import Link from "next/link";
import { useTranslation } from "react-i18next";
import { PageContainer } from "@/client/layout/PageContainer";
import { Card, CardContent } from "@/components/ui/card";
import { HelpHint } from "@/client/components/HelpHint";
import useBootstrapClient from "@/features/hooks/useBootstrapClient";

interface HelpPageProps {
  locale: string;
}

interface QuickLinkEntry {
  label: string;
  path: string;
}

interface FaqEntry {
  question: string;
  answer: string;
}

interface ContactEntry {
  label: string;
  value: string;
}

const ensureArray = <T,>(value: unknown): T[] => {
  if (Array.isArray(value)) {
    return value as T[];
  }
  return [];
};

const HelpPageClient = ({ locale }: HelpPageProps) => {
  useBootstrapClient();
  const { t } = useTranslation("pages");

  const quickLinks = ensureArray<QuickLinkEntry>(t("help.quickLinks", { returnObjects: true }));
  const faqEntries = ensureArray<FaqEntry>(t("help.faq", { returnObjects: true }));
  const contactEntries = ensureArray<ContactEntry>(t("help.contacts", { returnObjects: true }));
  const supportNote = t("help.supportNote", { defaultValue: "" }) as string;
  const headerTitle = t("help.header.title");
  const headerDescription = t("help.header.description");

  const sections = t("help.sections", { returnObjects: true }) as Record<string, string> | undefined;
  const quickLinksTitle = sections?.quickLinksTitle ?? "";
  const faqTitle = sections?.faqTitle ?? "";
  const supportTitle = sections?.supportTitle ?? "";

  const buildHref = (path: string) => {
    const normalized = path.startsWith("/") ? path.slice(1) : path;
    return `/${locale}/${normalized}`;
  };

  return (
    <PageContainer>
      {(headerTitle || headerDescription) && (
        <div className="flex flex-col gap-1">
          {headerTitle && <h1 className="text-2xl font-semibold">{headerTitle}</h1>}
          {headerDescription && (
            <p className="text-sm text-muted-foreground">{headerDescription}</p>
          )}
        </div>
      )}
      <div className="grid gap-2 grid-cols-1 [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]">
        <Card>
          <CardContent>
            <h3 className="text-base font-medium mt-0">{quickLinksTitle}</h3>
            <ul className="pl-6 my-1 list-disc">
              {quickLinks.map((link) => (
                <li key={link.path} className="text-[13px]">
                  <Link href={buildHref(link.path)} className="text-inherit no-underline">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            <HelpHint content={<p>{t("help.hint")}</p>} />
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <h3 className="text-base font-medium mt-0">{faqTitle}</h3>
            <div className="flex flex-col gap-1.5">
              {faqEntries.map((item) => (
                <details
                  key={item.question}
                  className="rounded-md px-2 py-2"
                  style={{ background: "var(--color-bg-muted,#2b364a)" }}
                >
                  <summary className="cursor-pointer font-medium">{item.question}</summary>
                  <p className="mt-1 text-sm leading-relaxed">{item.answer}</p>
                </details>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <h3 className="text-base font-medium mt-0">{supportTitle}</h3>
            <ul className="pl-6 my-1 list-disc">
              {contactEntries.map((contact) => (
                <li key={`${contact.label}-${contact.value}`} className="text-[13px]">
                  <strong>{contact.label}:</strong> {contact.value}
                </li>
              ))}
            </ul>
            {supportNote && <p className="text-xs text-muted-foreground">{supportNote}</p>}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
};

export default HelpPageClient;
